import { useEffect, useState, useCallback } from 'preact/hooks';
import { lookupsApi } from '@/api';
const cache = new Map();
const inFlight = new Map();
function cacheKey(workspaceId, webId) {
    return `${workspaceId}::${webId}`;
}
export function useEmailRuleLookup(workspaceId, webId) {
    const key = workspaceId && webId ? cacheKey(workspaceId, webId) : null;
    const [data, setData] = useState(key ? cache.get(key) ?? null : null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchRules = useCallback(async (force = false) => {
        if (!workspaceId || !webId) {
            setData(null);
            return;
        }
        const k = cacheKey(workspaceId, webId);
        if (!force && cache.has(k)) {
            setData(cache.get(k) ?? null);
            return;
        }
        const existing = inFlight.get(k);
        if (existing && !force) {
            const result = await existing;
            if (result)
                setData(result);
            return;
        }
        setIsLoading(true);
        setError(null);
        const promise = lookupsApi.getEmailRules(workspaceId, webId).then(response => {
            if (response.error || !response.data) {
                setError(response.error || 'Failed to load email rule lookup');
                return null;
            }
            cache.set(k, response.data);
            return response.data;
        }).finally(() => {
            inFlight.delete(k);
            setIsLoading(false);
        });
        inFlight.set(k, promise);
        const result = await promise;
        if (result)
            setData(result);
    }, [workspaceId, webId]);
    useEffect(() => {
        void fetchRules();
    }, [fetchRules]);
    const refetch = useCallback(() => {
        if (key)
            cache.delete(key);
        void fetchRules(true);
    }, [key, fetchRules]);
    return { data, isLoading, error, refetch };
}
